import * as React from "react"
import { cn } from "../../lib/utils"
import { formatMoney, parseMoney } from "../../utils/format"

export interface MoneyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  currency?: string
  onValueChange?: (value: number | null) => void
}

function formatIntegerPart(digits: string): string {
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

// Maps a cursor position from the stripped string (no dots) back to the formatted string
function strippedToFormattedPos(formatted: string, strippedPos: number): number {
  if (strippedPos === 0) return 0;
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] !== '.') {
      count++;
      if (count === strippedPos) return i + 1;
    }
  }
  return formatted.length;
}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      className,
      currency = "USD",
      value,
      onChange,
      onValueChange,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [localValue, setLocalValue] = React.useState<string>(() =>
      formatMoney(value as number | undefined)
    )

    React.useEffect(() => {
      const currentParsed = parseMoney(localValue);
      const propValue = value === undefined || value === null || value === '' ? undefined : Number(value);

      if (propValue !== undefined && propValue !== currentParsed) {
        setLocalValue(formatMoney(propValue));
      } else if (propValue === undefined) {
        if (localValue !== '') {
          setLocalValue('');
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputEl = e.target;
      const rawValue = inputEl.value;
      const cursorPos = inputEl.selectionStart ?? rawValue.length;

      if (rawValue === '') {
        setLocalValue('');
        onChange?.(e);
        onValueChange?.(null);
        return;
      }

      // Reject invalid characters
      if (!/^-?[\d.,]*$/.test(rawValue)) return;

      // Strip thousands dots to get the canonical form
      const stripped = rawValue.replace(/\./g, '');

      // Validate: optional minus, integer digits, optional comma + up to 2 decimal digits
      if (!/^-?\d*,?\d{0,2}$/.test(stripped)) return;

      // Calculate where the cursor lands in the stripped string
      const rawBeforeCursor = rawValue.slice(0, cursorPos);
      const dotsBeforeCursor = (rawBeforeCursor.match(/\./g) || []).length;
      const strippedCursorPos = cursorPos - dotsBeforeCursor;

      // Split into integer and decimal parts
      const commaIndex = stripped.indexOf(',');
      let integerPart: string;
      let decimalPart: string | null = null;

      if (commaIndex !== -1) {
        integerPart = stripped.slice(0, commaIndex);
        decimalPart = stripped.slice(commaIndex + 1);
      } else {
        integerPart = stripped;
      }

      // Format integer part with thousands separators
      const isNegative = integerPart.startsWith('-');
      const digits = integerPart.replace('-', '');
      let formattedInt = formatIntegerPart(digits);
      if (isNegative) formattedInt = '-' + formattedInt;

      const formatted = decimalPart !== null
        ? formattedInt + ',' + decimalPart
        : formattedInt;

      setLocalValue(formatted);

      // Restore cursor to the equivalent position in the formatted string
      const newCursorPos = strippedToFormattedPos(formatted, strippedCursorPos);
      requestAnimationFrame(() => {
        inputEl.setSelectionRange(newCursorPos, newCursorPos);
      });

      onChange?.(e);
      if (onValueChange) {
        onValueChange(parseMoney(formatted));
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseMoney(localValue);
      if (parsed !== null) {
        setLocalValue(formatMoney(parsed));
      } else if (localValue !== '') {
        setLocalValue('');
        onValueChange?.(null);
      }
      onBlur?.(e);
    }

    return (
      <div
        className={cn(
          "flex w-full min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm",
          "focus-within:ring-1 focus-within:ring-primary/50",
          "dark:border-neutral-600 dark:bg-neutral-900"
        )}
      >
        <div
          className={cn(
            "inline-flex shrink-0 items-center px-2 sm:px-4 min-w-fit",
            "bg-gray-50 border-r border-gray-200",
            "dark:bg-neutral-700 dark:border-neutral-600"
          )}
        >
          <span className="text-xs text-gray-500 sm:text-sm dark:text-neutral-300">$</span>
        </div>

        <input
          type="text"
          ref={ref}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className={cn(
            "min-w-0 flex-1 px-2 sm:px-4 py-2.5 sm:py-3 text-base sm:text-sm bg-transparent text-right tabular-nums",
            "outline-none focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:pointer-events-none",
            "dark:text-neutral-200 dark:placeholder-neutral-500",
            className
          )}
          autoComplete="off"
          {...props}
        />

        <div
          className={cn(
            "inline-flex shrink-0 items-center px-2 sm:px-4 min-w-fit",
            "bg-gray-50 border-l border-gray-200",
            "dark:bg-neutral-700 dark:border-neutral-600"
          )}
        >
          <span className="text-xs text-gray-500 sm:text-sm dark:text-neutral-300">
            {currency}
          </span>
        </div>
      </div>
    )
  }
)

MoneyInput.displayName = "MoneyInput"
